import {
  Building2,
  UsersRound,
  CalendarDays,
  Clock3,
  Palmtree,
  UserCheck,
  Nfc,
  QrCode,
  Bell,
  ShieldCheck,
  ChartNoAxesCombined,
  Warehouse,
  ShoppingCart,
  Truck,
  CarFront,
  ClipboardCheck,
  Wrench,
  SlidersHorizontal,
} from 'lucide-react';
import { hasAnyCapUnder, hasCap, canViewOperationalUsers } from '@/lib/capabilities';
import { NFC_UI_VISIBLE } from '@/lib/nfcUiVisibility';
import { canConfigureHaccp } from '@/features/haccp/haccpAccess';

export const SETTINGS_SECTIONS = [
  { id: 'company', title: 'Azienda' },
  { id: 'organization', title: 'Organizzazione' },
  { id: 'operations', title: 'Operatività' },
  { id: 'security', title: 'Sicurezza' },
  { id: 'management', title: 'Gestione' },
  { id: 'platform', title: 'Piattaforma' },
];

const available = (item) => ({ ...item, status: 'available', clickable: true });
const comingSoon = (item) => ({ ...item, status: 'coming_soon', clickable: false, route: null });

export const SETTINGS_ITEMS = [
  available({
    id: 'structure',
    title: 'Struttura aziendale',
    description: 'Gestisci aziende, locali e reparti in un’unica struttura organizzata.',
    section: 'company',
    icon: Building2,
    route: '/impostazioni/struttura-aziendale',
    canView: (me) => hasAnyCapUnder(me, 'company') || hasAnyCapUnder(me, 'location') || hasAnyCapUnder(me, 'department'),
  }),
  available({
    id: 'users-roles',
    title: 'Utenti e Accessi',
    description: 'Gestisci le persone e i loro accessi operativi',
    section: 'company',
    icon: UsersRound,
    route: '/personale',
    // The operational access flow is tenant-scoped and is also available to
    // every TITOLARE account. The CEO-only foundation console remains linked
    // separately from the global navigation.
    canView: canViewOperationalUsers,
  }),
  available({
    id: 'shifts-coverage',
    title: 'Turni e copertura',
    description: 'Configura regole, orari e copertura dei turni',
    section: 'organization',
    icon: CalendarDays,
    route: '/turni',
    canView: (me) => hasCap(me, 'turni.view') || hasCap(me, 'availability.self.manage'),
  }),
  available({
    id: 'shift-default-schedules',
    title: 'Orari predefiniti Turni',
    description: 'Configura gli orari proposti per giorno, servizio e reparto',
    section: 'organization',
    icon: Clock3,
    route: '/impostazioni/orari-turni',
    canView: (me) => hasCap(me, 'turni.view'),
  }),
  available({
    id: 'leave-coverage',
    title: 'Ferie e copertura',
    description: 'Configura regole ferie e copertura minima',
    section: 'organization',
    icon: Palmtree,
    route: '/ferie',
    canView: (me) => (
      hasCap(me, 'ferie.approve')
      || hasCap(me, 'ferie.balance')
      || hasCap(me, 'ferie.balance.manage')
      || hasAnyCapUnder(me, 'ferie.config')
    ),
  }),
  available({
    id: 'leave-minimum',
    title: 'Fabbisogno minimo ferie',
    description: 'Definisci il numero minimo di persone necessarie',
    section: 'organization',
    icon: UserCheck,
    route: '/impostazioni/ferie-fabbisogno',
    canView: (me) => hasCap(me, 'ferie.config.manage'),
  }),
  available({
    id: 'leave-policy',
    title: 'Policy ferie',
    description: 'Definisci fonte, validità e criterio di conteggio delle ferie',
    section: 'organization',
    icon: SlidersHorizontal,
    route: '/impostazioni/ferie-policy',
    canView: (me) => hasCap(me, 'ferie.view') || hasCap(me, 'ferie.config.manage'),
  }),
  available({
    id: 'attendance-nfc',
    title: 'Presenze · Punti NFC',
    description: 'Registra, verifica e gestisci i punti NFC fisici',
    section: 'operations',
    icon: Nfc,
    route: '/impostazioni/punti-nfc',
    canView: (me) => NFC_UI_VISIBLE && hasCap(me, 'attendance.*'),
  }),
  available({
    id: 'attendance-dynamic-qr',
    title: 'Punti di timbratura QR',
    description: 'Gestisci punti QR e dispositivi fissi per azienda e locale.',
    section: 'operations',
    icon: QrCode,
    route: '/impostazioni/timbratura-qr',
    canView: (me) => hasCap(me, 'attendance.view'),
  }),
  comingSoon({
    id: 'notifications',
    title: 'Notifiche',
    description: 'Configura notifiche e comunicazioni',
    section: 'operations',
    icon: Bell,
  }),
  available({
    id: 'audit',
    title: 'Audit e tracciabilità',
    description: 'Consulta attività, modifiche e registri di sistema',
    section: 'security',
    icon: ShieldCheck,
    route: '/audit',
    canView: (me) => hasCap(me, 'audit.view'),
  }),
  comingSoon({
    id: 'cost-control',
    title: 'Costi e controllo economico',
    description: 'Configura controllo costi, indicatori economici e impostazioni finanziarie',
    section: 'management',
    icon: ChartNoAxesCombined,
  }),
  comingSoon({
    id: 'warehouse',
    title: 'Magazzino',
    description: 'Configura gestione magazzino e scorte',
    section: 'management',
    icon: Warehouse,
  }),
  comingSoon({
    id: 'purchasing',
    title: 'Ordini e acquisti',
    description: 'Configura ordini, acquisti e processi di approvvigionamento',
    section: 'management',
    icon: ShoppingCart,
  }),
  comingSoon({
    id: 'suppliers',
    title: 'Fornitori',
    description: 'Gestisci fornitori e configurazioni di acquisto',
    section: 'management',
    icon: Truck,
  }),
  comingSoon({
    id: 'company-vehicles',
    title: 'Mezzi aziendali',
    description: 'Gestisci mezzi, veicoli e configurazioni correlate',
    section: 'management',
    icon: CarFront,
  }),
  available({
    id: 'haccp',
    title: 'Configurazione HACCP',
    description: 'Prepara attività e attrezzature per ciascun locale',
    section: 'operations',
    icon: ClipboardCheck,
    route: '/impostazioni/haccp/stato',
    canView: canConfigureHaccp,
  }),
  comingSoon({
    id: 'maintenance',
    title: 'Manutenzioni',
    description: 'Configura manutenzioni, scadenze e interventi',
    section: 'management',
    icon: Wrench,
  }),
  comingSoon({
    id: 'other',
    title: 'Altre configurazioni',
    description: 'Altre impostazioni generali della piattaforma',
    section: 'platform',
    icon: SlidersHorizontal,
  }),
];

export function visibleSettingsItems(me) {
  const tenantSections = new Set(['company', 'organization', 'operations', 'security']);
  const isGlobal = Boolean(me?.is_global);
  return SETTINGS_ITEMS.filter((item) => {
    if (item.id === 'attendance-nfc' && !NFC_UI_VISIBLE) return false;
    if (!isGlobal && !tenantSections.has(item.section)) return false;
    return item.status === 'coming_soon' || !item.canView || item.canView(me);
  });
}